import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { DIMENSIONS, LEVELS, questionText } from "../data/lightAssessment";

const pad = (n) => String(n).padStart(2, "0");

export const LightQuestions = ({ answers, setAnswer, onBack, onFinish }) => {
  const [i, setI] = useState(0);
  const dim = DIMENSIONS[i];
  const total = DIMENSIONS.length;
  const current = answers[dim.code];
  const isLast = i === total - 1;
  const answeredCount = DIMENSIONS.filter((d) => answers[d.code]).length;

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [i]);

  const goNext = () => {
    if (!current) return;
    if (isLast) onFinish();
    else setI((n) => n + 1);
  };
  const goBack = () => (i === 0 ? onBack() : setI((n) => n - 1));

  return (
    <div className="evc-faq" data-testid="light-questions">
      <div className="evc-faq-top">
        <div className="evc-faq-top-fill" style={{ width: `${(answeredCount / total) * 100}%` }} />
      </div>

      <div className="evc-faq-grid">
        <aside className="evc-faq-sections" data-testid="light-sections">
          <div className="evc-faq-sections-head">
            <span>Sections</span>
            <span data-testid="light-progress">{answeredCount}/{total}</span>
          </div>
          <ul>
            {DIMENSIONS.map((d, idx) => {
              const done = !!answers[d.code];
              const active = idx === i;
              return (
                <li
                  key={d.code}
                  className={`evc-faq-sec${active ? " active" : ""}${done ? " done" : ""}`}
                  onClick={() => setI(idx)}
                  data-testid={`light-section-${d.code}`}
                >
                  <span className="num">{pad(idx + 1)}</span>
                  <span className="label">{d.label}</span>
                  {done && <Check className="check" size={15} strokeWidth={3} />}
                </li>
              );
            })}
          </ul>
        </aside>

        <main className="evc-faq-main">
          <AnimatePresence mode="wait">
            <motion.div
              key={dim.code}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.26, ease: [0.2, 0.6, 0.2, 1] }}
            >
              <div className="evc-faq-head">
                <span className="evc-faq-dbadge">{dim.code}</span>
                <span className="evc-faq-section-label">Section {pad(i + 1)} / {pad(total)}</span>
              </div>
              <h2 className="evc-faq-title">{dim.label}</h2>
              <p className="evc-faq-sub">{dim.sub}</p>
              <div className="evc-faq-divider" />

              <div className="evc-faq-qrow">
                <span className="evc-faq-qnum">{pad(i + 1)}</span>
                <h3 className="evc-faq-question" data-testid="light-question-text">
                  {questionText(dim, null)}
                </h3>
              </div>

              <div className="evc-faq-options" data-testid="light-options">
                {dim.options.map((opt, idx) => {
                  const score = idx + 1;
                  const active = current === score;
                  const lvlName = LEVELS[idx]?.name || "";
                  return (
                    <button
                      key={opt.level}
                      className={`evc-faq-option${active ? " active" : ""}`}
                      onClick={() => setAnswer(dim.code, score)}
                      data-testid={`light-option-${dim.code}-${score}`}
                    >
                      <div className="evc-faq-opt-meta">
                        <span className="evc-faq-opt-num">{score}</span>
                        <span className="evc-faq-opt-level">{lvlName}</span>
                        <span className="evc-faq-opt-dots">
                          {[0, 1, 2, 3, 4].map((k) => (
                            <i key={k} className={k < score ? "on" : ""} />
                          ))}
                        </span>
                      </div>
                      <span className="evc-faq-opt-text">{opt.text}</span>
                      <span className="evc-faq-opt-radio">{active && <span className="dot" />}</span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="evc-faq-foot">
            <span className="evc-faq-foot-step">
              Question <b>{i + 1}</b> of <b>{total}</b>
            </span>
            <div className="evc-faq-foot-actions">
              <button className="evc-lite-ghost" onClick={goBack} data-testid="light-back-btn">
                <ArrowLeft size={16} /> Back
              </button>
              <button
                className="evc-btn evc-lite-next"
                onClick={goNext}
                disabled={!current}
                data-testid="light-next-btn"
              >
                {isLast ? "See my result" : "Continue"} <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};
